import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Paper,
    Container,
    LinearProgress
} from '@mui/material';
import {
    Person as PersonIcon,
    Spa as SpaIcon,
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';

/**
 * AppointmentTypeSelector - Étape 1 du flux de réservation
 * 
 * Permet au client de choisir entre :
 * - Rendez-vous simple (une prestation)
 * - Prestations consécutives (plusieurs soins enchaînés)
 */
const AppointmentTypeSelector = ({
    salonId,
    onSelectSimple,
    onSelectConsecutive,
    onBack,
    currentStep = 1,
    totalSteps = 4,
}) => {
    const { t } = useTranslation();
    const { tokens: theme } = useSalonPublicTheme();
    return (
        <Container
            data-testid="appointment-type-theme-root"
            maxWidth="lg"
            style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
            sx={{
            py: 0, // Suppression du padding vertical du conteneur
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            color: theme.salon.onBackground,
            mt: 0, // Suppression de l'espace en haut du conteneur
            [HIGH_DENSITY_TABLET_MEDIA]: {
                maxWidth: 'min(94vw, 1440px)',
                py: 1,
            },
        }}>
            {/* Bouton Retour */}
            <Box sx={{ 
                mb: 0,
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: 1200,
                mx: 'auto',
                px: { xs: 0, md: 0 },
                pt: 0, // Suppression de l'espace en haut du bouton
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1320,
                },
            }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{
                        color: theme.salon.onBackground,
                        textTransform: 'none',
                        fontWeight: 600,
                        pl: 0,
                        py: 0.5,
                        minWidth: 'auto',
                        '& .MuiButton-startIcon': {
                            marginRight: '4px',
                            marginLeft: '-4px'
                        },
                        '&:hover': {
                            backgroundColor: theme.salon.surfaceMuted,
                        }
                    }}
                >
                    {t('client_services.back')}
                </Button>
            </Box>

            {/* Contenu principal */}
            <Box sx={{ 
                flex: 1,
                maxWidth: 1200, 
                mx: 'auto',
                width: '100%',
                px: { xs: 1, md: 0 },
                pb: { xs: 12, md: 4 },
                pt: 1, // Petit espace entre le bouton et le contenu
                [HIGH_DENSITY_TABLET_MEDIA]: {
                    maxWidth: 1320,
                },
            }}>
                {/* Titre principal */}
                <Box sx={{ textAlign: 'center', mb: { xs: 1, md: 6 } }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: theme.salon.onBackground,
                        mb: 2,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            fontSize: 'clamp(2.5rem, 4vw, 3.2rem)',
                        },
                        lineHeight: 1.2,
                    }}
                >
                    {t('client_services.appointment_type_title')}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: theme.salon.onBackground,
                        opacity: 0.76,
                        maxWidth: 500,
                        mx: 'auto',
                        fontSize: '1rem',
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            maxWidth: 720,
                            fontSize: '1.1rem',
                        },
                    }}
                >
                    {t('client_services.appointment_type_subtitle')}
                </Typography>
            </Box>

            {/* Cards de sélection */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 4,
                    maxWidth: 900,
                    mx: 'auto',
                    mb: 5,
                    [HIGH_DENSITY_TABLET_MEDIA]: {
                        maxWidth: 1120,
                        gap: 5,
                    },
                }}
            >
                {/* Card RDV Simple */}
                <Paper
                    data-testid="appointment-type-simple-card"
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            p: 4,
                        },
                        borderRadius: 4,
                        border: `1px solid ${theme.salon.border}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                        backgroundColor: theme.salon.compatibilityMode ? '#ffffff' : theme.salon.surfaceMuted,
                        color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                        },
                    }}
                    onClick={onSelectSimple}
                >
                    {/* Icône circulaire */}
                    <Box
                        sx={{
                            width: { xs: 40, md: 56 },
                            height: { xs: 40, md: 56 },
                            borderRadius: '50%',
                            backgroundColor: theme.salon.primary,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: { xs: 1.5, md: 2.5 },
                        }}
                    >
                        <PersonIcon sx={{ fontSize: { xs: 20, md: 28 }, color: theme.salon.onPrimary }} />
                    </Box>

                    {/* Image */}
                    <Box
                        component="img"
                        src="/images/simple_appointment.png"
                        alt={t('client_services.simple_appointment')}
                        sx={{
                            width: '100%',
                            height: { xs: 120, md: 180 },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                height: 230,
                            },
                            objectFit: 'contain',
                            mb: { xs: 1.5, md: 2.5 },
                            borderRadius: 2,
                        }}
                    />

                    {/* Titre */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground,
                            textAlign: 'center',
                            mb: { xs: 0.75, md: 1.5 },
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1.45rem',
                            },
                        }}
                    >
                        {t('client_services.simple_appointment')}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.salon.compatibilityMode ? theme.colors.gray[500] : theme.salon.onBackground,
                            opacity: 0.78,
                            textAlign: 'center',
                            mb: { xs: 1.5, md: 2.5 },
                            lineHeight: 1.4,
                            minHeight: { xs: 'auto', md: 48 },
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1rem',
                                minHeight: 60,
                            },
                        }}
                    >
                        {t('client_services.simple_appointment_desc')}
                    </Typography>

                    {/* Bouton */}
                    <Button
                        variant="contained"
                        fullWidth
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectSimple();
                        }}
                        sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1.08rem',
                            },
                        }}
                    >
                        {t('client_services.select')}
                    </Button>
                </Paper>

                {/* Card Prestations Consécutives */}
                <Paper
                    data-testid="appointment-type-consecutive-card"
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 3 },
                        [HIGH_DENSITY_TABLET_MEDIA]: {
                            p: 4,
                        },
                        borderRadius: 4,
                        border: `1px solid ${theme.salon.border}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                        backgroundColor: theme.salon.compatibilityMode ? '#ffffff' : theme.salon.surfaceMuted,
                        color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                        },
                    }}
                    onClick={onSelectConsecutive}
                >
                    {/* Icône circulaire */}
                    <Box
                        sx={{
                            width: { xs: 40, md: 56 },
                            height: { xs: 40, md: 56 },
                            borderRadius: '50%',
                            backgroundColor: theme.salon.primary,
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: { xs: 1.5, md: 2.5 },
                        }}
                    >
                        <SpaIcon sx={{ fontSize: { xs: 20, md: 28 }, color: theme.salon.onPrimary }} />
                    </Box>

                    {/* Image */}
                    <Box
                        component="img"
                        src="/images/consecutive_services.png"
                        alt={t('client_services.consecutive_services')}
                        sx={{
                            width: '100%',
                            height: { xs: 120, md: 180 },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                height: 230,
                            },
                            objectFit: 'contain',
                            mb: { xs: 1.5, md: 2.5 },
                            borderRadius: 2,
                        }}
                    />

                    {/* Titre */}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: theme.salon.compatibilityMode ? theme.colors.gray[900] : theme.salon.onBackground,
                            textAlign: 'center',
                            mb: { xs: 0.75, md: 1.5 },
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1.45rem',
                            },
                        }}
                    >
                        {t('client_services.consecutive_services')}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.salon.compatibilityMode ? theme.colors.gray[500] : theme.salon.onBackground,
                            opacity: 0.78,
                            textAlign: 'center',
                            mb: { xs: 1.5, md: 2.5 },
                            lineHeight: 1.4,
                            minHeight: { xs: 'auto', md: 48 },
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1rem',
                                minHeight: 60,
                            },
                        }}
                    >
                        {t('client_services.consecutive_services_desc')}
                    </Typography>

                    {/* Bouton */}
                    <Button
                        variant="outlined"
                        fullWidth
                        endIcon={<ArrowForwardIcon fontSize="small" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelectConsecutive();
                        }}
                        sx={{
                            fontWeight: 600,
                            fontSize: '1rem',
                            [HIGH_DENSITY_TABLET_MEDIA]: {
                                fontSize: '1.08rem',
                            },
                        }}
                    >
                        {t('client_services.select')}
                    </Button>
                </Paper>
            </Box>

            {/* Note en bas de page */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                }}
            >
                <InfoIcon
                    sx={{
                        fontSize: 16,
                        color: theme.salon.onBackground,
                        opacity: 0.72,
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.salon.onBackground,
                        opacity: 0.72,
                    }}
                >
                    {t('client_services.note_changeable')}
                </Typography>
                </Box>
            </Box>
        </Container>
    );
};

export default AppointmentTypeSelector;
